<template>
    <div class="sm:w-full md:w4/5 lg:w-3/5 mx-auto md:p-6">
        <div class="bg-white rounded-xl shadow-sm  border-gray-200 overflow-hidden">
            <!-- Header -->
            <div class="bg-gradient-to-r from-red-500 to-pink-500 px-6 py-4">
                <h2 class="text-xl font-semibold text-white">Account Settings</h2>
                <p class="text-red-100 text-sm mt-1">Manage your account and preferences...</p>
            </div>

            <!-- Plan Section -->
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                <h2 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                        <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
/>
                        </svg>
                    </div>
                    Subscription Plan
                </h2>

                <div
v-if="userSettings.plan.value == 'paid'"
                    class="bg-green-50 border border-green-200 rounded-lg p-4"
>
                    <div class="flex items-center">
                        <div class="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                            <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M5 13l4 4L19 7"
/>
                            </svg>
                        </div>
                        <div>
                            <p class="font-semibold text-green-800">Premium Plan</p>
                            <p class="text-sm text-green-700">Thank you for your support!</p>
                        </div>
                    </div>
                </div>

                <div v-else class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div class="flex items-start justify-between">
                        <div class="flex-1">
                            <p class="font-semibold text-blue-800 mb-2">Free Plan</p>
                            <p class="text-sm text-blue-700 mb-4">
                                Upgrade for AUD$10 once off (no subscription) to track unlimited gifts and friends!
                            </p>
                            <button
@click="createCheckoutSession" :disabled="checkoutLoading"
                                class="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg text-sm transition-colors duration-200 disabled:opacity-50"
>
                                <svg
v-if="checkoutLoading" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                    xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
>
                                    <circle
class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                        stroke-width="4"
></circle>
                                    <path
class="opacity-75" fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
>
                                    </path>
                                </svg>
                                {{ checkoutLoading ? 'Processing...' : 'Upgrade Now' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Personal Settings Section -->
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                <h2 class="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                    <div class="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                        <svg class="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
/>
                        </svg>
                    </div>
                    Personal Information
                </h2>

                <div class="space-y-6">
                    <!-- Country -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                            Country
                        </label>
                        <select
v-model="userSettings.country.value"
                            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
>
                            <option v-for="option in userSettings.country.options" :value="option" :key="option">
                                {{ option }}
                            </option>
                        </select>
                        <p class="text-xs text-gray-500 mt-2">
                            We use this to determine the dates of holidays and events in your country. Can't see your
                            country?
                            <a class="text-blue-600 hover:text-blue-700" href="mailto:hello@presentplanner.co">Email
                                us</a> and we'll add it.
                        </p>
                    </div>

                    <!-- Email -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                            Email Address
                        </label>
                        <input
v-model="userSettings.email" type="email" :disabled="isFederatedUser" :class="[
                            'w-full px-3 py-2 border rounded-lg transition-colors duration-200',
                            isFederatedUser
                                ? 'border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed'
                                : 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                        ]"
>
                        <p v-if="isFederatedUser" class="text-xs text-gray-500 mt-2 flex items-center">
                            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
/>
                            </svg>
                            Email cannot be changed as you signed in with Google. To change your email, please update it
                            in your Google account.
                        </p>
                    </div>
                </div>
            </div>

            <!-- Event Reminders Section -->
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                <h2 class="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                    <div class="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
                        <svg
class="w-4 h-4 text-orange-600 lucide lucide-bell-ring-icon lucide-bell-ring"
                            xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
>
                            <path d="M10.268 21a2 2 0 0 0 3.464 0" />
                            <path d="M22 8c0-2.3-.8-4.3-2-6" />
                            <path
                                d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326"
/>
                            <path d="M4 2C2.8 3.7 2 5.7 2 8" />
                        </svg>
                    </div>
                    Event Reminders
                </h2>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div
v-for="i in userSettings.toggles" :key="i.id"
                        class="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200"
>
                        <span class="text-sm font-medium text-gray-700">{{ i.friendlyName }}</span>
                        <label class="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" class="sr-only peer" v-model="i.value" :checked="i.value">
                            <div
                                class="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-blue-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"
>
                            </div>
                        </label>
                    </div>
                </div>
            </div>

            <!-- Security Section -->
            <div v-if="!isFederatedUser" class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                <h2 class="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                    <div class="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                        <svg class="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
/>
                        </svg>
                    </div>
                    Change Password
                </h2>

                <div class="space-y-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                            Current Password
                        </label>
                        <input
v-model="userSettings.oldPassword" type="password"
                            placeholder="Enter your current password"
                            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
>
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                            New Password
                        </label>
                        <input
v-model="userSettings.newPassword" type="password" placeholder="Enter your new password"
                            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
>
                    </div>
                </div>
            </div>

            <!-- Federated User Info -->
            <div v-if="isFederatedUser" class="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
                <h2 class="text-lg font-semibold text-blue-900 mb-4 flex items-center">
                    <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                        <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
/>
                        </svg>
                    </div>
                    Google Account
                </h2>
                <p class="text-blue-800">
                    You're signed in with Google. To change your password or email, please manage your account directly
                    in Google.
                </p>
            </div>

            <!-- Error Message -->
            <div v-if="settingsError !== ''" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div class="flex items-center">
                    <svg class="w-5 h-5 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
/>
                    </svg>
                    <p class="text-sm font-medium text-red-800">{{ settingsError }}</p>
                </div>
            </div>

            <!-- Action Buttons -->
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                    <button
@click="updateUserAttributes()" type="button" :disabled="settingsSaved" :class="[
                        'inline-flex items-center px-6 py-3 rounded-lg font-medium text-sm transition-all duration-200',
                        settingsSaved
                            ? 'bg-green-600 hover:bg-green-700 text-white cursor-default'
                            : 'bg-blue-600 hover:bg-blue-700 text-white hover:shadow-md'
                    ]"
>
                        <svg
v-if="settingsSaved" class="w-4 h-4 mr-2" fill="none" stroke="currentColor"
                            viewBox="0 0 24 24"
>
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                        </svg>
                        {{ settingsSaved ? 'Settings Saved!' : 'Save Changes' }}
                    </button>

                    <button
@click="deletingUser = true" type="button"
                        class="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg text-sm transition-colors duration-200"
>
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
/>
                        </svg>
                        Delete Account
                    </button>
                </div>
            </div>

            <!-- Email Verification Section -->
            <div v-if="userVerification" class="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
                <h3 class="text-lg font-semibold text-blue-900 mb-4 flex items-center">
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
/>
                    </svg>
                    Email Verification Required
                </h3>
                <p class="text-blue-800 mb-4">
                    Your email address has changed. Please enter the verification code we sent to your new email
                    address.
                </p>

                <div class="space-y-4">
                    <div>
                        <label class="block text-sm font-medium text-blue-800 mb-2">
                            Verification Code
                        </label>
                        <input
v-model="userVerificationCode" type="text"
                            class="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
>
                    </div>

                    <div v-if="userVerificationSuccess" class="bg-green-100 border border-green-300 rounded-lg p-3">
                        <p class="text-green-800 font-medium">✓ Email verified successfully!</p>
                    </div>

                    <div v-if="userVerificationError" class="bg-red-100 border border-red-300 rounded-lg p-3">
                        <p class="text-red-800 font-medium">✗ Verification failed. Please try again.</p>
                    </div>

                    <button
@click="verifyEmailValidationCode()" type="button"
                        class="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg text-sm transition-colors duration-200"
>
                        Verify Email
                    </button>
                </div>
            </div>

            <!-- Account Deletion Confirmation -->
            <div v-if="deletingUser" class="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
                <h3 class="text-lg font-semibold text-red-900 mb-4 flex items-center">
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
/>
                    </svg>
                    Confirm Account Deletion
                </h3>
                <div class="bg-white border border-red-200 rounded-lg p-4 mb-4">
                    <p class="text-red-800 mb-2 font-medium">⚠️ This action cannot be undone</p>
                    <p class="text-red-700 text-sm">
                        Deleting your account will permanently remove all your gift and friend data.
                        You will also stop receiving reminder emails.
                    </p>
                </div>

                <div class="flex space-x-3">
                    <button
@click="deleteUser()" type="button"
                        class="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg text-sm transition-colors duration-200"
>
                        Yes, Delete My Account
                    </button>
                    <button
@click="deletingUser = false" type="button"
                        class="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg text-sm transition-colors duration-200"
>
                        Cancel
                    </button>
                </div>
            </div>

            <!-- Feedback Section -->
            <div class="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-xl p-6">
                <h3 class="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                    <svg class="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
/>
                    </svg>
                    We'd Love Your Feedback
                </h3>
                <p class="text-gray-700 mb-4">
                    Have questions, suggestions, or feedback? We're always looking to improve Present Planner!
                </p>
                <a
href="mailto:hello@presentplanner.co"
                    class="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg text-sm transition-colors duration-200"
>
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
/>
                    </svg>
                    Contact Us
                </a>
            </div>
        </div>
    </div>
</template>

<script>
import { Auth } from 'aws-amplify';

export default {
    name: "Account",
    props: {
    },
    components: {
    },
    computed: {
        isFederatedUser() {
            // We'll set this in the data and update it when component is created
            return this.federatedUserStatus;
        }
    },
    data() {
        return {
            checkoutLoading: false,
            federatedUserStatus: false, // Track federated user status
            userVerification: false,
            userVerificationCode: '',
            userVerificationError: false,
            userVerificationSuccess: false,
            userSettings: {
                plan: {
                    id: "custom:plan",
                    value: this.$store.state.user.data.plan,
                    friendlyName: "Plan",
                    options: ['Free', 'Premium']
                },
                country: {
                    id: "custom:country",
                    value: this.$store.state.user.data.country,
                    friendlyName: "Country",
                    options: ['Australia', 'Brazil', 'Canada', 'Fiji', 'France', 'India', 'Ireland', 'Mexico', 'Netherlands', 'Nigeria', 'New Zealand', 'Philippines', 'Scotland', 'Singapore', 'South Africa', 'UK', 'USA', 'Wales']
                },
                toggles: {
                    showMothersDay: {
                        id: "custom:show_mothers_day",
                        value: this.$store.state.user.data.show_mothers_day,
                        friendlyName: "Show Mother's Day"
                    },
                    showFathersDay: {
                        id: "custom:show_fathers_day",
                        value: this.$store.state.user.data.show_fathers_day,
                        friendlyName: "Show Father's Day"
                    },
                    remindMothersDay: {
                        id: "custom:remind_mothers_day",
                        value: this.$store.state.user.data.remind_mothers_day,
                        friendlyName: "Mother's Day Reminders"
                    },
                    remindFathersDay: {
                        id: "custom:remind_fathers_day",
                        value: this.$store.state.user.data.remind_fathers_day,
                        friendlyName: "Father's Day Reminders"
                    },
                    remindChristmas: {
                        id: "custom:remind_christmas",
                        value: this.$store.state.user.data.remind_christmas,
                        friendlyName: "Christmas Reminders"
                    },
                    remindEaster: {
                        id: "custom:remind_easter",
                        value: this.$store.state.user.data.remind_easter,
                        friendlyName: "Easter Reminders"
                    },
                    remindValentines: {
                        id: "custom:remind_valentines",
                        value: this.$store.state.user.data.remind_valentines,
                        friendlyName: "Valentine's Day Reminders"
                    },
                },
                email: this.$store.state.user.data.username,
                oldPassword: '',
                newPassword: '',
            },
            settingsSaved: false,
            settingsError: '',
            deletingUser: false,
        };
    },
    created() {
        Auth.currentAuthenticatedUser({
            bypassCache: true
        })
            .then((user) => {
                if (user.attributes['custom:plan'] !== this.$store.state.user.data.plan) {
                    this.$store.dispatch("fetchUser", user);
                }
                this.federatedUserStatus = this.checkFederatedUser(user);
            }
            )
            .catch((err) => console.log(err));
    },
    watch: {
        deletingUser(newVal) {
            if (newVal) {
                this.scrollToBottom();
            }
        }
    },
    methods: {
        async createCheckoutSession() {
            this.checkoutLoading = true;
            try {
                const response = await fetch('https://api.' + process.env.VUE_APP_ENVIRONMENT + '.presentplanner.co/create-checkout-session', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ email: this.$store.state.user.data.username })
                });
                const data = await response.json();
                const stripe = Stripe(process.env.VUE_APP_STRIPE_PUB_KEY);
                const result = await stripe.redirectToCheckout({ sessionId: data.id });
                if (result.error) {
                    console.error(result.error.message);
                }
            } catch (error) {
                console.error("Error: ", error);
            }
        },
        async verifyEmailValidationCode() {
            await Auth.verifyCurrentUserAttributeSubmit('email', this.userVerificationCode)
                .then(() => {
                    this.userVerificationSuccess = true;
                    Auth.currentAuthenticatedUser({})
                        .then((user) => {
                            this.$store.dispatch("fetchUser", user);
                        })
                })
                .catch((e) => {
                    this.userVerificationError = true;
                });
        },
        async updateUserAttributes() {
            var user = await Auth.currentAuthenticatedUser();
            await Auth.updateUserAttributes(user, {
                'custom:country': this.userSettings.country.value.toString(),
                'custom:show_mothers_day': this.userSettings.toggles.showMothersDay.value.toString(),
                'custom:show_fathers_day': this.userSettings.toggles.showFathersDay.value.toString(),
                'custom:remind_mothers_day': this.userSettings.toggles.remindMothersDay.value.toString(),
                'custom:remind_fathers_day': this.userSettings.toggles.remindFathersDay.value.toString(),
                'custom:remind_christmas': this.userSettings.toggles.remindChristmas.value.toString(),
                'custom:remind_easter': this.userSettings.toggles.remindEaster.value.toString(),
                'custom:remind_valentines': this.userSettings.toggles.remindValentines.value.toString(),
            }).then((response) => {
                this.settingsSaved = true;
                Auth.currentAuthenticatedUser({})
                    .then((user) => {
                        this.$store.dispatch("fetchUser", user);
                    }).catch((err) => console.log(err));
            }).catch((error) => {
                this.settingsError = error.message;
            });

            if (this.userSettings.newPassword !== '') {
                Auth.currentAuthenticatedUser()
                    .then((user) => {
                        Auth.changePassword(user, this.userSettings.oldPassword, this.userSettings.newPassword).then((response) => {
                        }).catch((error) => {
                        });
                    }).catch((err) => console.log(err));
            };

            if (this.userSettings.email !== this.$store.state.user.data.username) {
                Auth.updateUserAttributes(user, {
                    email: this.userSettings.email,
                })
                    .then(() => {
                        this.userVerification = true;
                    })
                    .catch((e) => {
                        console.log('failed with error', e);
                    });
            }
        },
        async deleteUser() {
            try {
                const result = await Auth.deleteUser();
                this.$store.dispatch("fetchUser");
                this.$store.dispatch("resetState");
                this.$router.push("/");
            } catch (error) {
                console.log('Error deleting user', error);
            }
        },
        scrollToBottom() {
            window.scrollTo({
                top: document.documentElement.scrollHeight,
                behavior: 'smooth'
            });
        },
        checkFederatedUser(user) {
            // Check if user signed in via OAuth (Google, etc.)
            // Federated users will have identities array with provider info
            if (user && user.attributes.identities) {
                try {
                    // Parse the JSON string into an array
                    const identities = JSON.parse(user.attributes.identities);
                    return identities.some(identity =>
                        identity.providerType === 'Google' ||
                        identity.providerType === 'Facebook' ||
                        identity.providerType === 'SAML' ||
                        identity.provider !== 'Cognito'
                    );
                } catch (error) {
                    console.error('Error parsing identities:', error);
                    return false;
                }
            }
            return false;
        }
    },
};
</script>